import { createContext, useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'
import type { ContactJson } from 'app/types/entities'
import type { SchedulingLocation } from 'app/types/scheduling'
import type { DraftRequest } from './types'

const LOCATION_FIELDS = `id pmsIntegrated kollaConsumerId isSchedulerLive isAIAgentLive primaryTrackingNumber`

export type SchedulingDockValue = {
  /** Async because switching away from an in-progress booking asks for confirmation. */
  openDock: (contact: ContactJson) => Promise<void>
  closeDock: () => void
  setMinimized: (minimized: boolean) => void
  requestDraft: (text: string, phone?: string) => void
  dockContact?: ContactJson
  minimized: boolean
  draftRequest?: DraftRequest
  location?: SchedulingLocation
  /**
   * The location snapshotted at the moment the dock opened for `dockContact`,
   * not the live, inbox-scoped `location` above. `location` keeps following
   * whatever locationId the inbox is currently scoped to (see the effect
   * below), including while this dock stays open and the staff member
   * navigates to a different location's thread (e.g. via a MessageAlertCard).
   * `getScheduleEligibility` guarantees `location` is the contact's own
   * location at the instant `openDock` commits, so capturing it there is
   * exactly the snapshot the open wizard should keep reading from.
   */
  dockLocation?: SchedulingLocation
}

export const SchedulingDockContext = createContext<SchedulingDockValue>({
  openDock: async () => {},
  closeDock: () => {},
  setMinimized: () => {},
  requestDraft: () => {},
  dockContact: undefined,
  minimized: false,
  draftRequest: undefined,
  location: undefined,
  dockLocation: undefined,
})

export function SchedulingDockProvider({
  locationId,
  children,
}: {
  locationId?: string
  children: ReactNode
}): ReactElement {
  const [confirm] = useConfirmation()
  const [dockContact, setDockContact] = useState<ContactJson | undefined>()
  const [dockLocation, setDockLocation] = useState<SchedulingLocation | undefined>()
  const [minimized, setMinimized] = useState(false)
  const [draftRequest, setDraftRequest] = useState<DraftRequest | undefined>()
  const [location, setLocation] = useState<SchedulingLocation | undefined>()

  // One request per location, shared by every contact card. Cards read it through
  // useCanSchedule rather than each fetching their own copy.
  useEffect(() => {
    // Clear immediately on every locationId change, including the fetch-in-flight
    // window before the new one lands. Otherwise a stale location keeps satisfying
    // useCanSchedule and a Schedule button can look eligible for the location the
    // user just left. If the new fetch errors, this also stays cleared rather than
    // silently keeping the old location.
    setLocation(undefined)
    if (!locationId) {
      return
    }
    let cancelled = false
    gqlFetchOne<SchedulingLocation>(`location`, locationId, LOCATION_FIELDS)
      .then((loaded) => {
        if (!cancelled) setLocation(loaded ?? undefined)
      })
      .catch((error) => {
        logError(`SchedulingDockProvider: failed to fetch location`, error)
      })
    return () => {
      cancelled = true
    }
  }, [locationId])

  const openDock = useCallback(
    async (contact: ContactJson) => {
      if (dockContact && dockContact.id !== contact.id) {
        // Defensive: useConfirmationAlert's onClose only sets open(false) and never
        // calls the captured reject, so in production this promise never actually
        // rejects on cancel; it stays pending forever and this catch never runs.
        // That is still correct, because the code below simply never reaches
        // setDockContact. Kept in case the shared hook starts rejecting on cancel.
        try {
          await confirm({
            variant: `danger`,
            title: `Switch appointment?`,
            description: `You’re setting up an appointment for ${dockContact.name}. Switching to ${contact.name} discards it.`,
            submitLabel: `Switch`,
            cancelLabel: `Keep going`,
          })
          // oxlint-disable-next-line local/require-catch-logging
        } catch {
          return
        }
      }
      setDockContact(contact)
      // Snapshot the live location now, while getScheduleEligibility guarantees
      // it is still the contact's own location. The dock reads this instead of
      // the live `location`, so it does not drift if the inbox scopes to a
      // different location while this dock stays open.
      setDockLocation(location)
      setMinimized(false)
    },
    [confirm, dockContact, location],
  )

  const closeDock = useCallback(() => {
    setDockContact(undefined)
    setDockLocation(undefined)
    setMinimized(false)
  }, [])

  const requestDraft = useCallback((text: string, phone?: string) => {
    if (!text) return
    setDraftRequest((prev) => ({ text, id: (prev?.id ?? 0) + 1, phone }))
  }, [])

  const value = useMemo(
    () => ({
      openDock,
      closeDock,
      setMinimized,
      requestDraft,
      dockContact,
      minimized,
      draftRequest,
      location,
      dockLocation,
    }),
    [openDock, closeDock, requestDraft, dockContact, minimized, draftRequest, location, dockLocation],
  )

  return <SchedulingDockContext.Provider value={value}>{children}</SchedulingDockContext.Provider>
}
